import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Routes, getChartURL } from "~/routes";
import { BreadcrumbItem, Breadcrumbs } from "~/components/Breadcrumbs";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { RedirectToLoginWhenNotAuthenticated } from "~/modules/auth/components/RedirectToLoginWhenNotAuthenticated";
import { ChartFetcher } from "~/modules/charts/components/ChartFetcher";
import { LoadingBoundary } from "~/components/LoadingBoundary";
import { ChartIndividualPage } from "~/modules/charts/pages/individual";
import { EmptyState } from "~/modules/charts/pages/list/EmptyState";
import { isNullOrUndefined } from "~/utils/isNullOrUndefined";
import { useParams } from "next/navigation";

// // // //

export default function ChartIndividual() {
    const params = useParams();
    const chartSlug = params?.slug;

    // Handle undefined [slug] path parameter
    if (isNullOrUndefined(chartSlug)) {
        return null;
    }

    return (
        <RedirectToLoginWhenNotAuthenticated>
            <PageTrack route={Routes.chartIndividual}>
                <ChartFetcher chartSlug={String(chartSlug)}>
                    {({ loading, chart }) => {
                        if (loading) {
                            return (
                                <LoadingBoundary loading={loading}>
                                    <p className="sr-only">Loading Chart...</p>
                                </LoadingBoundary>
                            );
                        }

                        const chartTitle =
                            chart?.title || chart?.slug || "Not found";

                        const SEO_TITLE = chart
                            ? `Charts | ${chartTitle}`
                            : "Charts | Not found";

                        const pageUrl = addBaseUrl(
                            getChartURL({ slug: chart?.slug || "" }),
                        );

                        // Assembble SEO Breadcrumb Items
                        const seoBreadcrumbs: Array<{
                            name: string;
                            href: string;
                        }> = [
                            {
                                name: "Charts",
                                href: Routes.charts,
                            },
                        ];

                        // Assemble nav breadcrumb items
                        const navBreadcrumbs: Array<BreadcrumbItem> = [
                            {
                                name: "Charts",
                                href: Routes.charts,
                            },
                        ];

                        // Add chart title to SEO breadcrumbs + nav breadcrumbs
                        if (chart) {
                            seoBreadcrumbs.push({
                                name: chartTitle,
                                href: getChartURL({ slug: chart?.slug || "" }),
                            });

                            navBreadcrumbs.push({
                                name: chartTitle,
                                href: getChartURL({ slug: chart?.slug || "" }),
                                current: true,
                            });
                        } else {
                            navBreadcrumbs.push({
                                name: "Not Found",
                                href: Routes.charts,
                                current: true,
                            });
                        }

                        return (
                            <>
                                <NextSeo
                                    title={SEO_TITLE}
                                    canonical={pageUrl}
                                />
                                <BreadcrumbSEO items={seoBreadcrumbs} />

                                <UserFetcher>
                                    {({ user }) => (
                                        <Navbar
                                            route={Routes.chartIndividual}
                                            user={user}
                                        />
                                    )}
                                </UserFetcher>
                                <SidebarLayout pathname={Routes.charts}>
                                    <Breadcrumbs pages={navBreadcrumbs} />
                                    <PageWrapper>
                                        {!chart && (
                                            <EmptyState
                                                title="This chart isn't here"
                                                description="It may have been deleted, renamed, or you might not have access. Browse your charts or start a new one."
                                                className="mt-6"
                                                notFound={true}
                                            />
                                        )}

                                        {chart && (
                                            <ChartIndividualPage
                                                chart={chart}
                                            />
                                        )}
                                    </PageWrapper>
                                </SidebarLayout>
                            </>
                        );
                    }}
                </ChartFetcher>
            </PageTrack>
        </RedirectToLoginWhenNotAuthenticated>
    );
}
